/* eslint-disable @typescript-eslint/no-explicit-any */
/** @jsx jsx */
/** @jsxRuntime classic */

import { Interpolation, jsx } from '@emotion/react';
import React, { CSSProperties, useMemo, useCallback } from 'react';
import ButtonGroupContext from '../ButtonGroup/ButtonGroupContext';
import { ButtonEvent, OnClickEvent } from '../events/EventProvider';
import useEvents from '../events/useEvents';
import { ThemeConfig } from '../style/theme';
import { TypographyVariant } from '../style/typography';
import useTheme from '../style/useTheme';
import { hex2rgba } from '../utils';
import { SxProp } from '../style/sxProp';
import { ColorResolveOptions } from '../style/colors';

export type Props = React.PropsWithChildren<{
  variant?: 'solid' | 'outline' | 'ghost';
  typography?: TypographyVariant;
  color?: 'primary' | 'secondary' | 'neutral';
  backgroundColor?: ColorResolveOptions;
  skipThemeSx?: boolean;
  weight?: 'normal' | 'bold';
  dataTestId?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  role?: React.AriaRole;
  disabled?: boolean;
  style?: CSSProperties;
  'aria-label'?: string;
  'aria-selected'?: React.ButtonHTMLAttributes<HTMLButtonElement>['aria-selected'];
  eventId?: string;
  sx?: SxProp;
  href?: string;
}>;

const DEFAULT_ID = 'Button';

const Button: React.FC<Props> = (componentProps: Props) => {
  const {
    theme,
    useThemeNext,
    isDarkMode,
    resolveTypography,
    resolveCSS,
    resolveColor,
  } = useTheme();
  const contextProps = React.useContext(ButtonGroupContext);
  const props = { ...contextProps, ...componentProps };
  const {
    eventId,
    dataTestId,
    variant = 'solid',
    'aria-label': ariaLabel,
    'aria-selected': ariaSelected,
    color = 'primary',
    backgroundColor,
    skipThemeSx = false,
    weight = 'bold',
    typography = 'label',
    type = 'submit',
    role,
    children,
    onClick,
    sx,
    href,
    disabled,
    ...rest
  } = props;

  const triggerEvent = useEvents<OnClickEvent<ButtonEvent, EventTarget>>(
    eventId,
    'Button'
  );

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(e);
    }
    triggerEvent({
      target: e.target,
    });
  };

  const buttonColor: ColorResolveOptions = useMemo(
    () =>
      backgroundColor && color === 'primary'
        ? backgroundColor
        : {
            type: 'component',
            value: theme.components.button[color].color,
          },
    [backgroundColor, color, theme.components.button]
  );
  const themeSx = skipThemeSx
    ? {}
    : useThemeNext
    ? theme.components.button[color].sx?.next
    : theme.components.button[color].sx?.legacy;

  const buttonTextColor: ColorResolveOptions = useMemo(
    () =>
      variant === 'solid' ||
      theme.components.button[color].outlineAndGhostColorSwap?.value
        ? {
            type: 'component',
            value: theme.components.button[color].onColor,
          }
        : {
            type: 'component',
            value: theme.components.button[color].color,
          },
    [color, theme.components.button, variant]
  );

  const resolveColorWithOpacity = useCallback(
    (colorType: 'button' | 'base' | 'shadow', opacity: number) => {
      if (colorType === 'button') {
        return variant !== 'solid' &&
          theme.components.button[color].outlineAndGhostColorSwap?.value
          ? hex2rgba(resolveColor(buttonTextColor), opacity)
          : hex2rgba(resolveColor(buttonColor), opacity);
      }
      if (colorType === 'shadow') {
        return resolveColor({
          type: 'theme',
          colorLegacy: 'Neutral/100',
          colorNext: 'background/shadow',
          opacityLegacy: opacity,
          opacityNext: opacity,
        });
      }
      return resolveColor({
        type: 'theme',
        colorLegacy: 'Neutral/0',
        colorNext: 'background/default',
        opacityLegacy: opacity,
        opacityNext: opacity,
      });
    },
    [
      buttonColor,
      resolveColor,
      theme.components.button,
      color,
      variant,
      buttonTextColor,
    ]
  );

  const buttonShadow = useMemo(() => {
    if (isDarkMode) return 'none';
    const baseShadow = `0 2px 4px ${resolveColorWithOpacity('shadow', 0.1)}`;
    if (variant === 'solid') {
      return `${baseShadow}, inset 0 0 0 1.2px ${resolveColorWithOpacity(
        'shadow',
        0.15
      )}`;
    }
    if (variant === 'outline') {
      return baseShadow;
    }
    return 'none';
  }, [variant, resolveColorWithOpacity, isDarkMode]);

  const css: Interpolation<ThemeConfig> = resolveCSS({
    ...resolveTypography(typography),
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'pre-wrap',
    background: backgroundColor
      ? resolveColor(backgroundColor)
      : variant === 'solid'
      ? resolveColor(buttonColor)
      : 'transparent',
    height: '48px',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    display: '-webkit-box',
    minHeight: '48px',
    width: '100%',
    borderRadius: '8px',
    boxShadow: buttonShadow,
    border: 'none',
    cursor: 'pointer',
    color: buttonTextColor,
    '&:not(:last-child)': {
      marginBottom: 2,
    },
    ...(!disabled && {
      '&:hover': {
        background: `linear-gradient(0deg, ${resolveColorWithOpacity(
          'base',
          0.1
        )}, ${resolveColorWithOpacity('base', 0.1)}),
      ${resolveColorWithOpacity('button', 1)}`,
      },
    }),
    '&:active': {
      background: `linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), ${resolveColorWithOpacity(
        'button',
        1
      )}`,
    },
    '&:focus': {
      outline: `solid 2px ${resolveColorWithOpacity('button', 0.25)}`,
    },
    '&:disabled': {
      /*
       *  Don't allow cursor pointer when disabled
       */
      cursor: 'default',
      opacity: 0.5,
    },
    ...(variant === 'outline' && {
      border: `1px solid ${resolveColorWithOpacity('button', 0.7)}`,
      ...(!disabled && {
        '&:hover': {
          background: `linear-gradient(0deg, ${resolveColorWithOpacity(
            'button',
            0.1
          )}, ${resolveColorWithOpacity(
            'button',
            0.1
          )}), ${resolveColorWithOpacity('base', 0.1)}`,
        },
        '&:active': {
          background: `linear-gradient(0deg, ${resolveColorWithOpacity(
            'button',
            0.3
          )}, ${resolveColorWithOpacity(
            'button',
            0.3
          )}), ${resolveColorWithOpacity('base', 0.1)}`,
        },
        '&:focus': {
          background: `linear-gradient(0deg, ${resolveColorWithOpacity(
            'button',
            0.1
          )}, ${resolveColorWithOpacity(
            'button',
            0.1
          )}), ${resolveColorWithOpacity('base', 0.1)}`,
        },
      }),
    }),
    ...(variant === 'ghost' &&
      !disabled && {
        '&:hover': {
          background: resolveColorWithOpacity('button', 0.1),
        },
        '&:active': {
          background: resolveColorWithOpacity('button', 0.2),
        },
        '&:focus': {
          background: resolveColorWithOpacity('button', 0.2),
          outline: `2px solid ${resolveColorWithOpacity('button', 0.7)}`,
        },
      }),
    ...(weight === 'bold' && {
      fontWeight: 600,
    }),
    ...(href && {
      display: 'inline-flex',
      textDecoration: 'none',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
    }),
    ...(themeSx as any),
    ...(sx as any),
  });

  return !href ? (
    <button
      className="origami-Button"
      css={css}
      // eslint-disable-next-line react/button-has-type
      type={type}
      role={role}
      data-testid={dataTestId || DEFAULT_ID}
      aria-label={ariaLabel}
      aria-selected={ariaSelected}
      {...rest}
      onClick={handleClick}
      disabled={disabled}
    >
      {children}
    </button>
  ) : (
    <a
      className="origami-Button"
      css={css}
      role={role}
      target="_blank"
      rel="noopener noreferrer"
      href={href}
      data-testid={dataTestId || DEFAULT_ID}
      aria-label={ariaLabel}
    >
      {children}
    </a>
  );
};

export default Button;
